import {
  attachmentTypeOptions,
  EmailAttachment,
  InteractiveElement,
} from '@/client/courses/types/courses.type';
import { ContentInlineFormattingToolbar } from '@/components/courses/ContentInlineFormattingToolbar';
import { setupColorPickerForBubble } from '@/ui/rich-text';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// TODO: move to '@/ui/rich-text' — react-quill is being confined there
// so the editor can be swapped in one place.
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import styled, { css } from 'styled-components';

const EmailEditorContainer = styled.div<{ readOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 300px;
  flex-shrink: 0;
  background: var(--white-main);
  border-radius: 19px;
  border: 1px solid var(--gray-dark);
  width: 300px;

  &:before {
    content: '';
    border-radius: 19px 19px 0 0;
    background: var(--purple-dark);
    height: 26px;
  }

  ${({ readOnly }) =>
    readOnly &&
    css`
      .quill {
        &:hover {
          border-color: var(--white-main) !important;
        }
      }
    `}
`;

const EmailHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  padding-top: 6px;
  background: var(--purple-main);
  color: var(--white-main);
  min-height: 55px;
  border-bottom: 1px solid var(--gray-dark);
`;

const EmailAttachmentContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  padding: 13px;
  background: var(--white-main);
  border-bottom: 1px solid var(--gray-light);
  flex-wrap: wrap;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  border-radius: 2px;
  width: 100%;
  min-height: 30px;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-darker);
  font-size: var(--xsmall-font-size);
  padding: 0;

  &:hover {
    color: var(--red-main);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AttachmentText = styled.textarea`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: 150%;
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  cursor: text;
  resize: none;
  overflow: hidden;
  min-height: 20px;
  padding: 0;

  &:focus {
    background: var(--white-main);
    border: 1px solid var(--gray-dark);
  }

  &:disabled,
  &[readonly] {
    cursor: default;
  }
`;

const StyledHeaderQuill = styled(ReactQuill)`
  border: 1px solid var(--white-main);

  .ql-container {
    border: none;
    font-size: var(--small-font-size);
    padding: 0;
    border-radius: 0;
  }

  .ql-editor {
    color: var(--black-main);
    min-height: 55px;
    background: var(--white-main);

    p {
      margin: 0;
      color: var(--black-main);
    }

    * {
      font-size: var(--xsmall-font-size) !important;
      line-height: var(--xsmall-line-height) !important;
    }

    &:focus {
      outline: none;
    }
  }

  .ql-bubble {
    border: none;
  }

  .ql-container.ql-bubble {
    border: none;
  }
`;

const EmailBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--small-padding);
  background: var(--white-main);
  height: 100%;
  border-bottom-right-radius: var(--semi-medium-border-radius);
  border-bottom-left-radius: var(--semi-medium-border-radius);
`;

const StyledBodyQuill = styled(ReactQuill)`
  border: 1px solid var(--white-main);

  .ql-editor {
    min-height: 250px;
    height: 100%;
    font-size: var(--small-font-size);
    line-height: 1.6;
    border-radius: 0;
    background: var(--white-main);

    * {
      font-size: var(--small-font-size) !important;
      line-height: 1.6 !important;
    }

    p {
      color: var(--black-main);
    }
  }
`;

type ContentEmailComponentProps = {
  id?: string;
  interactiveElement: InteractiveElement;
  onUpdate?: (data: {
    header?: string;
    body?: string;
    attachment?: EmailAttachment;
  }) => void;
  readOnly?: boolean;
};

type EmailData = {
  header?: string;
  body?: string;
  attachment?: EmailAttachment;
};

const isQuillContentEmpty = (html?: string) => {
  if (!html) return true;

  const normalized = html
    .replace(/<br\s*\/?>(?=\s*<\/p>)/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return normalized.length === 0;
};

export const ContentEmailComponent: React.FC<ContentEmailComponentProps> = ({
  id,
  interactiveElement,
  onUpdate,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const headerQuillRef = useRef<any>(null);
  const bodyQuillRef = useRef<any>(null);
  const attachmentTextRef = useRef<HTMLTextAreaElement>(null);
  const [emailData, setEmailData] = useState<EmailData>({
    header: interactiveElement.data?.header,
    body: interactiveElement.data?.body,
    attachment: interactiveElement.data?.attachment,
  });

  useEffect(() => {
    setEmailData({
      header: interactiveElement.data?.header,
      body: interactiveElement.data?.body,
      attachment: interactiveElement.data?.attachment,
    });
  }, [interactiveElement.data, id]);

  useEffect(() => {
    if (readOnly) return;

    const cleanupHeader = headerQuillRef.current
      ? setupColorPickerForBubble(
          headerQuillRef,
          'custom-color-btn-header',
          'custom-color-input-header',
        )
      : undefined;
    const cleanupBody = bodyQuillRef.current
      ? setupColorPickerForBubble(
          bodyQuillRef,
          'custom-color-btn-body',
          'custom-color-input-body',
        )
      : undefined;

    return () => {
      cleanupHeader?.();
      cleanupBody?.();
    };
  }, [readOnly, headerQuillRef.current, bodyQuillRef.current, id]);

  const handleHeaderChange = (
    html: string,
    source: 'user' | 'api' | 'silent',
  ) => {
    const shouldIgnoreDestructiveExternalUpdate =
      source !== 'user' &&
      !headerQuillRef.current?.hasFocus() &&
      !isQuillContentEmpty(emailData.header) &&
      isQuillContentEmpty(html);

    if (shouldIgnoreDestructiveExternalUpdate) {
      return;
    }

    const newData = { ...emailData, header: html };
    setEmailData(newData);
    onUpdate?.(newData);
  };

  const handleBodyChange = (
    html: string,
    source: 'user' | 'api' | 'silent',
  ) => {
    const shouldIgnoreDestructiveExternalUpdate =
      source !== 'user' &&
      !bodyQuillRef.current?.hasFocus() &&
      !isQuillContentEmpty(emailData.body) &&
      isQuillContentEmpty(html);

    if (shouldIgnoreDestructiveExternalUpdate) {
      return;
    }

    const newData = { ...emailData, body: html };
    setEmailData(newData);
    onUpdate?.(newData);
  };

  const attachment = emailData.attachment;

  const getAttachmentIcon = (type: string) => {
    const option = attachmentTypeOptions(t).find((opt) => opt.value === type);
    return option?.icon;
  };

  const handleAttachmentLabelChange = (newLabel: string) => {
    if (readOnly) return;
    if (!emailData.attachment) return;

    const updatedData = {
      ...emailData,
      attachment: {
        ...emailData.attachment,
        label: newLabel ? newLabel : undefined,
      },
    };
    setEmailData(updatedData);
    onUpdate?.(updatedData);

    if (attachmentTextRef.current) {
      attachmentTextRef.current.style.height = 'auto';
      attachmentTextRef.current.style.height = `${attachmentTextRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (attachmentTextRef.current) {
      attachmentTextRef.current.style.height = 'auto';
      attachmentTextRef.current.style.height = `${attachmentTextRef.current.scrollHeight}px`;
    }
  }, [emailData.attachment?.label]);

  const handleRemoveAttachment = () => {
    if (readOnly) return;
    if (!emailData.attachment) return;

    const updatedData = {
      ...emailData,
      attachment: undefined,
    };
    setEmailData(updatedData);
    onUpdate?.(updatedData);
  };

  return (
    <EmailEditorContainer
      key={id}
      readOnly={readOnly}
      className="notranslate"
      translate="no"
      lang="en"
      data-translate="no"
    >
      {!readOnly && (
        <>
          <ContentInlineFormattingToolbar
            toolbarId="inline-formatting-toolbar-header"
            colorButtonId="custom-color-btn-header"
            colorInputId="custom-color-input-header"
          />
          <ContentInlineFormattingToolbar
            toolbarId="inline-formatting-toolbar-body"
            colorButtonId="custom-color-btn-body"
            colorInputId="custom-color-input-body"
          />
        </>
      )}

      <EmailHeader>
        <StyledHeaderQuill
          key={`email-header-${id}`}
          className="rich-text-editor rich-text-editor-v2 notranslate"
          value={emailData.header || ''}
          theme="bubble"
          readOnly={readOnly}
          modules={{
            toolbar: readOnly
              ? false
              : { container: '#inline-formatting-toolbar-header' },
            clipboard: {
              matchVisual: false,
            },
          }}
          onChange={(html, _delta, source) => {
            if (!readOnly) {
              handleHeaderChange(html, source);
            }
          }}
          ref={(el) => {
            if (el && headerQuillRef.current !== el.getEditor()) {
              const editor = el.getEditor();
              editor.root.setAttribute('translate', 'no');
              editor.root.setAttribute('data-translate', 'no');
              editor.root.classList.add('notranslate');
              headerQuillRef.current = editor;
            }
          }}
        />
      </EmailHeader>

      {attachment && (
        <EmailAttachmentContainer>
          <AttachmentItem key={attachment.id}>
            {getAttachmentIcon(attachment.type)}
            <AttachmentText
              ref={attachmentTextRef}
              value={attachment.label || ''}
              placeholder={
                !readOnly
                  ? t('content.email.attachment.placeholder')
                  : undefined
              }
              onChange={(e) => handleAttachmentLabelChange(e.target.value)}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
              readOnly={readOnly}
              disabled={readOnly}
              rows={1}
            />
            {!readOnly && (
              <DeleteButton onClick={handleRemoveAttachment} type="button">
                <i className="pi pi-times" />
              </DeleteButton>
            )}
          </AttachmentItem>
        </EmailAttachmentContainer>
      )}

      <EmailBodyContainer>
        <StyledBodyQuill
          key={`email-body-${id}`}
          value={emailData.body || ''}
          theme="bubble"
          className="rich-text-editor rich-text-editor-v2 notranslate"
          readOnly={readOnly}
          modules={{
            toolbar: readOnly
              ? false
              : { container: '#inline-formatting-toolbar-body' },
            clipboard: {
              matchVisual: false,
            },
          }}
          onChange={(html, _delta, source) => {
            if (!readOnly) {
              handleBodyChange(html, source);
            }
          }}
          ref={(el) => {
            if (el && bodyQuillRef.current !== el.getEditor()) {
              const editor = el.getEditor();
              editor.root.setAttribute('translate', 'no');
              editor.root.setAttribute('data-translate', 'no');
              editor.root.classList.add('notranslate');
              bodyQuillRef.current = editor;
            }
          }}
        />
      </EmailBodyContainer>
    </EmailEditorContainer>
  );
};
